<template>
  <div class="market">
    <div class="market-header">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索资源..."
        class="search-input"
        @keyup.enter="handleSearch"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
    </div>

    <el-tabs v-model="activeType" @tab-change="handleTypeChange">
      <el-tab-pane label="应用" name="app"></el-tab-pane>
      <el-tab-pane label="知识库" name="knowledge"></el-tab-pane>
      <el-tab-pane label="工具" name="tool"></el-tab-pane>
      <el-tab-pane label="MCP" name="mcp"></el-tab-pane>
    </el-tabs>

    <div class="filters">
      <el-select v-model="selectedCategory" placeholder="选择分类" clearable @change="handleFilterChange">
        <el-option
          v-for="cat in categories"
          :key="cat.id"
          :label="cat.name"
          :value="cat.id"
        />
      </el-select>
      <el-select v-model="selectedTags" placeholder="选择标签" multiple clearable @change="handleFilterChange">
        <el-option
          v-for="tag in tags"
          :key="tag.id"
          :label="tag.name"
          :value="tag.id"
        />
      </el-select>
    </div>

    <div class="market-items" v-loading="loading">
      <el-card v-for="item in items" :key="item.id" class="market-item-card">
        <div class="item-header">
          <div class="item-title-row">
            <h3>{{ item.name }}</h3>
            <el-tag v-if="item.is_pinned" type="warning" size="small" effect="dark">置顶</el-tag>
          </div>
          <el-tag :type="getTypeColor(item.type)">{{ getTypeName(item.type) }}</el-tag>
        </div>
        <p class="item-description">{{ getExcerpt(item.description) }}</p>
        <div class="item-meta">
          <span v-if="item.category">分类: {{ item.category.name }}</span>
          <div class="item-tags">
            <el-tag v-for="tag in item.tags" :key="tag.id" size="small" style="margin-right: 5px;">
              {{ tag.name }}
            </el-tag>
          </div>
        </div>
        <div class="item-stats">
          <span>下载: {{ item.download_count }}</span>
          <span>浏览: {{ item.view_count }}</span>
        </div>
        <div class="item-actions">
          <el-button type="primary" @click="goToDetail(item.id)">查看详情</el-button>
        </div>
      </el-card>

      <div v-if="items.length === 0" class="empty-container">
        <el-empty description="暂无数据" />
      </div>

      <el-pagination
        :current-page="currentPage"
        :page-sizes="[20, 50, 100]"
        :page-size="pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        class="pagination"
      />
    </div>
  </div>
</template>

<script>
import api from '../api'
import { Search } from '@element-plus/icons-vue'

export default {
  name: 'MarketPage',
  components: {
    Search
  },
  data() {
    return {
      activeType: 'app',
      searchKeyword: '',
      items: [],
      categories: [],
      tags: [],
      selectedCategory: null,
      selectedTags: [],
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false
    }
  },
  mounted() {
    this.loadCategories()
    this.loadTags()
    this.loadItems()
  },
  methods: {
    async loadCategories() {
      try {
        const response = await api.get('/market/categories/')
        // 处理分页响应格式
        if (Array.isArray(response.data)) {
          this.categories = response.data
        } else {
          this.categories = response.data.results || []
        }
        console.log('加载分类成功:', this.categories.length, '个分类')
      } catch (error) {
        console.error('加载分类失败:', error)
        this.categories = []
      }
    },
    async loadTags() {
      try {
        const response = await api.get('/market/tags/')
        // 处理分页响应格式
        if (Array.isArray(response.data)) {
          this.tags = response.data
        } else {
          this.tags = response.data.results || []
        }
        console.log('加载标签成功:', this.tags.length, '个标签')
      } catch (error) {
        console.error('加载标签失败:', error)
        this.tags = []
      }
    },
    async loadItems() {
      this.loading = true
      try {
        const params = new URLSearchParams()
        params.append('type', this.activeType)
        params.append('page', this.currentPage)
        params.append('page_size', this.pageSize)
        
        if (this.searchKeyword) {
          params.append('search', this.searchKeyword)
        }
        if (this.selectedCategory) {
          params.append('category', this.selectedCategory)
        }
        // 添加标签过滤 - 手动添加多个 tags 参数，确保格式为 tags=1&tags=2
        if (this.selectedTags && this.selectedTags.length > 0) {
          this.selectedTags.forEach(tagId => {
            params.append('tags', tagId)
          })
        }
        
        const response = await api.get(`/market/items/?${params.toString()}`)
        // 处理分页响应格式
        if (Array.isArray(response.data)) {
          // 如果没有分页，直接使用数组
          this.items = response.data
          this.total = response.data.length
        } else {
          // 如果有分页，使用results和count
          this.items = response.data.results || []
          this.total = response.data.count || 0
        }
      } catch (error) {
        console.error('加载资源失败:', error)
        this.items = []
        this.total = 0
        this.$message.error('加载资源失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    handleTypeChange() {
      this.currentPage = 1
      this.loadItems()
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadItems()
    },
    handleSearch() {
      this.currentPage = 1
      this.loadItems()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadItems()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadItems()
    },
    goToDetail(id) {
      this.$router.push(`/market/${id}`)
    },
    getTypeName(type) {
      const map = {
        app: '应用',
        knowledge: '知识库',
        tool: '工具',
        mcp: 'MCP'
      }
      return map[type] || type
    },
    getTypeColor(type) {
      const map = {
        app: 'success',
        knowledge: 'info',
        tool: 'warning',
        mcp: 'danger'
      }
      return map[type] || ''
    },
    getExcerpt(text) {
      if (!text) return ''
      return text.length > 100 ? text.substring(0, 100) + '...' : text
    }
  }
}
</script>

<style scoped>
.market {
  width: 100%;
  color: #e0e0e0;
}

.market-header {
  margin-bottom: 20px;
}

.search-input {
  max-width: 400px;
}

.filters {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.filters .el-select {
  flex: 1;
  max-width: 300px;
}

.market-items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.market-item-card {
  transition: all 0.3s;
}

.market-item-card:hover {
  box-shadow: 0 4px 20px rgba(0, 150, 255, 0.3) !important;
  transform: translateY(-2px);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.item-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.item-header h3 {
  margin: 0;
  flex: 1;
  color: #e0e0e0;
}

.item-description {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 10px;
  min-height: 40px;
}

.item-meta {
  margin-bottom: 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.item-tags {
  margin-top: 5px;
}

.item-stats {
  margin-bottom: 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.item-stats span {
  margin-right: 15px;
}

.item-actions {
  display: flex;
  gap: 10px;
}

.pagination {
  margin-top: 20px;
  text-align: center;
  grid-column: 1 / -1;
}

.empty-container {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}
</style>

